import React, { useEffect, useState } from "react";
import { ExternalLink, FileText, X } from "lucide-react";
import {
  getRentalFileName,
  getRentalFileUrl,
  isRentalImageFile,
} from "../../utils/rentalFilePreview";

const RentalFilePreview = ({
  file = null,
  saved = null,
  onRemove,
  label,
  className = "",
}) => {
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");

  useEffect(() => {
    if (!file) {
      setLocalPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setLocalPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const fileName = getRentalFileName(file, saved);
  const remoteUrl = getRentalFileUrl(file, saved);
  const previewUrl = localPreviewUrl || remoteUrl;
  const hasFile = Boolean(file || saved?.url || saved?.fileName || saved?.name);

  if (!hasFile) return null;

  const isImage = isRentalImageFile(file, saved);

  if (isImage && previewUrl) {
    return (
      <div className={`mt-3 ${className}`}>
        {label ? (
          <p className="mb-2 text-xs font-medium text-[#374151]">{label}</p>
        ) : null}
        <div className="relative overflow-hidden rounded-lg border border-[#E5E7EB] bg-[#F9FAFB]">
          <img
            src={previewUrl}
            alt={fileName}
            className="max-h-52 w-full object-contain"
          />
          <div className="flex items-center justify-between gap-2 border-t border-[#E5E7EB] bg-white px-3 py-2">
            <p className="truncate text-xs text-[#6B7280]">{fileName}</p>
            <div className="flex shrink-0 items-center gap-2">
              {previewUrl ? (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-[#097FE6] hover:underline"
                >
                  Open
                </a>
              ) : null}
              {onRemove ? (
                <button
                  type="button"
                  onClick={onRemove}
                  className="rounded p-1 text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#1A1A1A]"
                  aria-label="Remove file"
                >
                  <X className="h-4 w-4" strokeWidth={2} />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`mt-3 flex items-center gap-3 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-3 ${className}`}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-[#097FE6] shadow-sm">
        <FileText className="h-5 w-5" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        {label ? (
          <p className="text-xs font-medium text-[#374151]">{label}</p>
        ) : null}
        <p className="truncate text-sm font-medium text-[#000929]">{fileName}</p>
        <p className="text-xs text-[#6B7280]">Document</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {previewUrl ? (
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-[#097FE6] hover:underline"
          >
            View
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
          </a>
        ) : null}
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="rounded p-1 text-[#6B7280] hover:bg-[#E5E7EB] hover:text-[#1A1A1A]"
            aria-label="Remove file"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default RentalFilePreview;
